import { MonoTypeOperatorFunction, timer, throwError } from 'rxjs';
import { mergeMap, retryWhen } from 'rxjs/operators';
import { get as _get } from 'lodash';

// Retry-on-throttle for platform and internal APIs.
//
// Lifted from combined-orders.component.ts, where it was written for the
// billing-request lookups. The finance endpoints need the same behaviour and
// are rate-limited harder than the order endpoints, so it lives here rather
// than being copied a third time.

// Status 0 is the one that is not obvious: a browser reports a *throttled CORS
// preflight* as status 0, not 429, because the failed OPTIONS means the real
// response never arrives to carry its status code. Treating it as a transport
// error rather than throttling makes a rate-limited sync look like a network
// outage and gives up when it should back off.
export const isThrottleError = (error: unknown): boolean => {
    const status = _get(error, 'status');
    if (status === 429 || status === 503 || status === 0) {
        return true;
    }
    // Rate limiting that arrives with no HTTP status at all.
    //
    // Two separate sources produce this. AWS SSM Parameter Store throttling
    // reaches us as `[internal] Rate exceeded` through the failure envelope.
    // The marketplaces are worse: they answer HTTP 200 with a non-zero code in
    // the body, so the delegate throws `[36009002] Too many requests for
    // downstream` — a plain Error with no status anywhere on it.
    //
    // Matching text is unlovely, but it is the only signal present, and the
    // cost of missing one is that the most retryable error in the system is
    // treated as fatal. Compared case-insensitively against the vocabularies
    // these services actually use, all observed in production:
    //
    //   AWS      Rate exceeded · ThrottlingException · TooManyRequestsException
    //   TikTok   36009002 "Too many requests for downstream"
    //   Lazada   ApiCallLimit · ServerBusy
    //   Shopee   error_rate_limit
    //
    // Deliberately NOT matched: a bare "limit exceeded", which covers quota and
    // size errors that will fail identically on every attempt.
    const message = String(_get(error, 'message') ?? '').toLowerCase();
    return THROTTLE_SIGNALS.some((signal) => message.includes(signal));
};

const THROTTLE_SIGNALS = [
    'rate exceeded',
    'throttlingexception',
    'toomanyrequests',
    'too many requests',
    'rate limit',
    'error_rate_limit',
    'apicalllimit',
    'server busy',
    'serverbusy',
    // TikTok's downstream-throttle code, in case the wording is localised.
    '36009002',
];

// How many finance calls one store may have in flight at once.
//
// Deliberately small. A busy month is ~10 escrow-detail chunks plus ~10
// order-detail chunks per store, and the default unbounded mergeMap fires all
// of them the moment the order list resolves — across five stores at once.
// That burst is what earns a 429, and no amount of retrying fixes a request
// pattern that is itself the problem.
//
// Apply this per *call*, never around a paginating method: retrying the whole
// sequence on a late 429 re-issues every page that already succeeded, adding
// load at exactly the moment the platform is asking for less.
export const FINANCE_FETCH_CONCURRENCY = 2;

// How many stores may sync at once.
//
// This was 1 for a while, because the binding constraint was not the
// marketplaces but AWS SSM Parameter Store: the delegates read 3-4 credential
// parameters on every invoke against a 40 TPS account-wide default, so five
// stores in parallel put 60-80 parameter reads in flight and failed three
// stores outright with `Rate exceeded`.
//
// @disorn1/param-store-utils 1.2.0 caches those values for the life of a warm
// container, so that ceiling no longer binds — verified in production on
// 2026-08-05 with 12 rapid-fire delegate invokes and no throttling.
//
// Kept at 3 rather than 5 because the constraint below it is now our own API
// Gateway usage plan, and the dev stage is 10 rps / 100 burst — a tenth of
// prod. A value that only works against prod would fail exactly where changes
// get tested first. Raise it once dev's plan is widened.
export const STORE_SYNC_CONCURRENCY = 3;

// Linear backoff — 1s, 2s, 3s … — rather than exponential. These are
// per-shop quotas that refill on a short fixed window, so waiting minutes
// buys nothing over waiting seconds, and a sync the operator is watching
// should not stall for the length of an exponential tail.
//
// Only throttling is retried. A 401, a bad signature, or a malformed request
// will fail identically on every attempt, and retrying them just delays the
// error the operator needs to see.
export const retryOnThrottle = <T>(
    maxRetries: number = 5,
): MonoTypeOperatorFunction<T> =>
    retryWhen<T>((errors) =>
        errors.pipe(
            mergeMap((error, attempt) =>
                attempt < maxRetries && isThrottleError(error)
                    ? timer(RETRY_BASE_MS * (attempt + 1))
                    : throwError(error),
            ),
        ),
    );

// 1.5s, 3s, 4.5s, 6s, 7.5s — about 22s of patience across five attempts.
//
// Was 1s over three attempts, which is enough for our own gateway but not for
// a marketplace: TikTok's `Too many requests for downstream` refills on its
// own schedule, and giving up after 6s turned a wait into a failed store.
// A sync is operator-triggered and already minutes long, so the extra seconds
// cost nothing next to re-running the whole thing.
const RETRY_BASE_MS = 1500;
